import {
  type Active,
  type ClientRect,
  type CollisionDetection,
  closestCenter,
  DndContext,
  type DragEndEvent,
  type DraggableAttributes,
  DragOverlay,
  type DragStartEvent,
  type DroppableContainer,
  KeyboardCode,
  KeyboardSensor,
  type Modifier,
  MouseSensor,
  type Over,
  pointerWithin,
  rectIntersection,
  TouchSensor,
  useDndContext,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  horizontalListSortingStrategy,
  SortableContext,
  type SortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GlobalStyles } from '@mui/material';
import {
  type CSSProperties,
  createContext,
  type MutableRefObject,
  type ReactElement,
  type ReactNode,
  type SyntheticEvent,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

/**
 * A small drag-and-drop layer over dnd-kit that keeps the render-prop API of
 * react-beautiful-dnd: a DragDropContext holds Droppable lists of Draggable
 * items, and drops are reported as a source and destination list and index.
 */

export type DraggableLocation = {
  droppableId: string;
  index: number;
};

export type DropResult = {
  draggableId: string;
  source: DraggableLocation;
  /** Null when the item was dropped outside every list. */
  destination: DraggableLocation | null;
};

export type DroppableProvided = {
  innerRef: (element: HTMLElement | null) => void;
  droppableProps: { 'data-droppable-id': string };
  /** Items keep their place in the DOM while dragged, so nothing is needed here. */
  placeholder: ReactNode;
};

export type DroppableStateSnapshot = {
  isDraggingOver: boolean;
};

type DragHandleListener = (event: SyntheticEvent) => void;

export type DragHandleProps = DraggableAttributes & {
  'data-drag-handle': '';
  onMouseDown?: DragHandleListener;
  onTouchStart?: DragHandleListener;
  onKeyDown?: DragHandleListener;
};

export type DraggableProvided = {
  innerRef: (element: HTMLElement | null) => void;
  draggableProps: { style: CSSProperties; 'data-draggable-id': string };
  dragHandleProps: DragHandleProps;
};

export type DraggableStateSnapshot = {
  isDragging: boolean;
};

const ACTIVATION_DISTANCE_PX = 5;
const TOUCH_ACTIVATION_DELAY_MS = 120;
const DRAGGING_Z_INDEX = 1;

const DRAG_HANDLE_SELECTOR = '[data-drag-handle]';

/** The cursor and touch behaviour react-beautiful-dnd gave every drag handle. */
const handleStyles = (
  <GlobalStyles
    styles={{
      [DRAG_HANDLE_SELECTOR]: {
        WebkitTouchCallout: 'none',
        WebkitTapHighlightColor: 'transparent',
        touchAction: 'manipulation',
      },
      [`${DRAG_HANDLE_SELECTOR}:not([aria-disabled='true'])`]: {
        cursor: 'grab',
      },
    }}
  />
);

/** Keeps the grabbing cursor wherever the pointer goes, and stops text selection. */
const draggingStyles = (
  <GlobalStyles
    styles={{
      'body, body *': { cursor: 'grabbing !important' },
      body: { userSelect: 'none' },
    }}
  />
);

const INTERACTIVE_TAG_NAMES = new Set([
  'INPUT',
  'BUTTON',
  'TEXTAREA',
  'SELECT',
  'OPTION',
  'OPTGROUP',
  'VIDEO',
  'AUDIO',
]);

/**
 * Whether the event started on a control inside the drag handle, so pressing
 * a button or selecting text in a field does not pick the item up. The handle
 * itself may be a button.
 */
const isEventInInteractiveElement = (event: SyntheticEvent): boolean => {
  for (
    let element = event.target instanceof Element ? event.target : null;
    element && element !== event.currentTarget;
    element = element.parentElement
  ) {
    if (
      INTERACTIVE_TAG_NAMES.has(element.tagName) ||
      (element instanceof HTMLElement && element.isContentEditable)
    ) {
      return true;
    }
  }
  return false;
};

type ListData = { droppableId: string; size: number };

const getListData = (
  container: Active | Over | DroppableContainer,
): ListData | undefined => container.data.current?.list;

const getListId = (droppableId: string) => `droppable:${droppableId}`;

const toLocation = (target: Active | Over): DraggableLocation | null => {
  const sortable = target.data.current?.sortable;
  if (sortable) {
    return { droppableId: String(sortable.containerId), index: sortable.index };
  }
  const list = getListData(target);
  return list ? { droppableId: list.droppableId, index: list.size } : null;
};

/**
 * Picks the list under the pointer (or under the dragged item, for the
 * keyboard), then the item in it closest to the dragged one. An empty list is
 * a target in its own right.
 */
const collisionDetection: CollisionDetection = (args) => {
  const lists = args.droppableContainers.filter(getListData);
  const pointerHits = pointerWithin({ ...args, droppableContainers: lists });
  const [listHit] = pointerHits.length
    ? pointerHits
    : rectIntersection({ ...args, droppableContainers: lists });
  if (!listHit) return [];

  const droppableId = lists.find(({ id }) => id === listHit.id)?.data.current
    ?.list.droppableId;
  const items = args.droppableContainers.filter(
    ({ data }) => data.current?.sortable?.containerId === droppableId,
  );
  return items.length
    ? closestCenter({ ...args, droppableContainers: items })
    : [listHit];
};

type ListEntry = {
  direction: 'vertical' | 'horizontal';
  node: MutableRefObject<HTMLElement | null>;
};

type ListRegistry = Map<string, ListEntry>;

const ListRegistryContext = createContext<ListRegistry | null>(null);

/** Whether the dragged item is shown by the overlay rather than in place. */
const OverlayContext = createContext(false);

/**
 * A copy of the dragged element for the overlay, taken as it is rendered
 * while dragging, so it looks the same without rendering it a second time.
 */
const DragClone = () => {
  const { activeNode } = useDndContext();
  const cloneInto = useCallback(
    (container: HTMLElement | null) => {
      if (!container || !activeNode) return;
      const clone = activeNode.cloneNode(true) as HTMLElement;
      Object.assign(clone.style, {
        transform: '',
        transition: '',
        opacity: '',
        margin: '0',
        width: '100%',
        boxSizing: 'border-box',
      });
      container.replaceChildren(clone);
    },
    [activeNode],
  );
  return <div ref={cloneInto} />;
};

const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max);

export type DragDropContextProp = {
  onDragEnd: (result: DropResult) => void;
  /**
   * Holds the dragged item to its list's axis, and inside the list when it
   * is the only one. Turn off where items move between lists side by side.
   */
  lockAxis?: boolean;
  children: ReactNode;
};

export const DragDropContext = ({
  onDragEnd,
  lockAxis = true,
  children,
}: DragDropContextProp) => {
  const lists = useRef<ListRegistry>(new Map()).current;
  // dnd-kit only moves an item in place while it is over its own list, so
  // where it can go to another list it is shown by an overlay instead.
  const [dragMode, setDragMode] = useState<'inPlace' | 'overlay' | null>(null);
  // Measured when the drag starts, like the item's own rect, so both move
  // together when the page scrolls.
  const sourceListRect = useRef<ClientRect | null>(null);

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: { distance: ACTIVATION_DISTANCE_PX },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: TOUCH_ACTIVATION_DELAY_MS,
        tolerance: ACTIVATION_DISTANCE_PX,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
      // Enter keeps its usual meaning on the handle; space picks it up.
      keyboardCodes: {
        start: [KeyboardCode.Space],
        cancel: [KeyboardCode.Esc],
        end: [KeyboardCode.Space, KeyboardCode.Enter, KeyboardCode.Tab],
      },
    }),
  );

  const getSourceList = (active: Active | null) =>
    lists.get(String(active?.data.current?.sortable?.containerId));

  const handleDragStart = ({ active }: DragStartEvent) => {
    setDragMode(lists.size > 1 ? 'overlay' : 'inPlace');
    const node = getSourceList(active)?.node.current;
    sourceListRect.current =
      lists.size === 1 && node ? node.getBoundingClientRect() : null;
  };

  const lockToList: Modifier = ({ transform, active, activeNodeRect }) => {
    const list = getSourceList(active);
    if (!lockAxis || !list) return transform;
    const listRect = sourceListRect.current;
    if (list.direction === 'horizontal') {
      return {
        ...transform,
        x:
          listRect && activeNodeRect
            ? clamp(
                transform.x,
                listRect.left - activeNodeRect.left,
                listRect.right - activeNodeRect.right,
              )
            : transform.x,
        y: 0,
      };
    }
    return {
      ...transform,
      x: 0,
      y:
        listRect && activeNodeRect
          ? clamp(
              transform.y,
              listRect.top - activeNodeRect.top,
              listRect.bottom - activeNodeRect.bottom,
            )
          : transform.y,
    };
  };

  /**
   * Over an item in another list, the drop goes before or after that item
   * depending on which side of its middle the dragged item is, so it can also
   * be added at the end. Within one list dnd-kit's index is already where the
   * item ends up.
   */
  const getDestination = (
    active: Active,
    over: Over | null,
    source: DraggableLocation,
  ): DraggableLocation | null => {
    const destination = over ? toLocation(over) : null;
    const dragged = active.rect.current.translated;
    if (
      !over ||
      !destination ||
      !over.data.current?.sortable ||
      destination.droppableId === source.droppableId ||
      !dragged
    ) {
      return destination;
    }
    const isHorizontal =
      lists.get(destination.droppableId)?.direction === 'horizontal';
    const draggedCenter = isHorizontal
      ? dragged.left + dragged.width / 2
      : dragged.top + dragged.height / 2;
    const overCenter = isHorizontal
      ? over.rect.left + over.rect.width / 2
      : over.rect.top + over.rect.height / 2;
    return draggedCenter > overCenter
      ? { ...destination, index: destination.index + 1 }
      : destination;
  };

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    setDragMode(null);
    const source = toLocation(active);
    if (!source) return;
    onDragEnd({
      draggableId: String(active.id),
      source,
      destination: getDestination(active, over, source),
    });
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={collisionDetection}
      modifiers={[lockToList]}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={() => setDragMode(null)}
    >
      {handleStyles}
      {dragMode && draggingStyles}
      <ListRegistryContext.Provider value={lists}>
        <OverlayContext.Provider value={dragMode === 'overlay'}>
          {children}
        </OverlayContext.Provider>
      </ListRegistryContext.Provider>
      <DragOverlay dropAnimation={null}>
        {dragMode === 'overlay' && <DragClone />}
      </DragOverlay>
    </DndContext>
  );
};

type DroppableContextValue = {
  register: (draggableId: string, index: number) => () => void;
};

const DroppableContext = createContext<DroppableContextValue | null>(null);

/**
 * Leaves a list alone while the dragged item is outside it. dnd-kit would
 * otherwise shift its items as though the dragged one were being moved within
 * it.
 */
const withinListOnly =
  (strategy: SortingStrategy): SortingStrategy =>
  (args) =>
    args.activeIndex === -1 || args.overIndex === -1 ? null : strategy(args);

const verticalStrategy = withinListOnly(verticalListSortingStrategy);
const horizontalStrategy = withinListOnly(horizontalListSortingStrategy);

export type DroppableProp = {
  droppableId: string;
  direction?: 'vertical' | 'horizontal';
  children: (
    provided: DroppableProvided,
    snapshot: DroppableStateSnapshot,
  ) => ReactElement;
};

export const Droppable = ({
  droppableId,
  direction = 'vertical',
  children,
}: DroppableProp) => {
  // Draggables report their index, so the list order follows the `index`
  // each one is rendered with.
  const [indexes, setIndexes] = useState<ReadonlyMap<string, number>>(
    new Map(),
  );
  const register = useCallback((draggableId: string, index: number) => {
    setIndexes((current) => {
      if (current.get(draggableId) === index) return current;
      return new Map(current).set(draggableId, index);
    });
    return () =>
      setIndexes((current) => {
        if (current.get(draggableId) !== index) return current;
        const next = new Map(current);
        next.delete(draggableId);
        return next;
      });
  }, []);
  const items = useMemo(
    () =>
      [...indexes.entries()]
        .sort(([, a], [, b]) => a - b)
        .map(([draggableId]) => draggableId),
    [indexes],
  );
  const contextValue = useMemo(() => ({ register }), [register]);

  const list: ListData = { droppableId, size: items.length };
  const { setNodeRef, node, over } = useDroppable({
    id: getListId(droppableId),
    data: { list },
  });
  const lists = useContext(ListRegistryContext);
  useLayoutEffect(() => {
    lists?.set(droppableId, { direction, node });
    return () => {
      lists?.delete(droppableId);
    };
  }, [lists, droppableId, direction, node]);
  const isDraggingOver =
    !!over &&
    (getListData(over)?.droppableId === droppableId ||
      over.data.current?.sortable?.containerId === droppableId);

  return (
    <DroppableContext.Provider value={contextValue}>
      <SortableContext
        id={droppableId}
        items={items}
        strategy={
          direction === 'horizontal' ? horizontalStrategy : verticalStrategy
        }
      >
        {children(
          {
            innerRef: setNodeRef,
            droppableProps: { 'data-droppable-id': droppableId },
            placeholder: null,
          },
          { isDraggingOver },
        )}
      </SortableContext>
    </DroppableContext.Provider>
  );
};

export type DraggableProp = {
  draggableId: string;
  index: number;
  isDragDisabled?: boolean;
  /** Lets a drag start from a button or field inside the handle. */
  disableInteractiveElementBlocking?: boolean;
  children: (
    provided: DraggableProvided,
    snapshot: DraggableStateSnapshot,
  ) => ReactElement;
};

export const Draggable = ({
  draggableId,
  index,
  isDragDisabled = false,
  disableInteractiveElementBlocking = false,
  children,
}: DraggableProp) => {
  const droppable = useContext(DroppableContext);
  if (!droppable) {
    throw new Error('Draggable must be rendered inside a Droppable');
  }
  const { register } = droppable;
  const isOverlaid = useContext(OverlayContext);
  useLayoutEffect(
    () => register(draggableId, index),
    [register, draggableId, index],
  );

  const {
    setNodeRef,
    attributes,
    listeners,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: draggableId, disabled: isDragDisabled });

  const handleListeners = Object.fromEntries(
    Object.entries(listeners ?? {}).map(([eventName, listener]) => [
      eventName,
      (event: SyntheticEvent) => {
        if (
          !disableInteractiveElementBlocking &&
          isEventInInteractiveElement(event)
        ) {
          return;
        }
        listener(event);
      },
    ]),
  );

  return children(
    {
      innerRef: setNodeRef,
      draggableProps: {
        style: {
          transform: CSS.Translate.toString(transform),
          transition,
          ...(isDragging &&
            (isOverlaid
              ? // Holds the item's place while the overlay shows it moving.
                { opacity: 0 }
              : { position: 'relative', zIndex: DRAGGING_Z_INDEX })),
        },
        'data-draggable-id': draggableId,
      },
      dragHandleProps: {
        ...attributes,
        ...handleListeners,
        'data-drag-handle': '',
      },
    },
    { isDragging },
  );
};
